import {
  ActionPlanCollaboratorSchema,
  ActionPlanCollaboratorsSchema,
  ActionPlanInputSchema,
  ActionPlanSchema,
  ActionPlanTaskSchema,
  ActionPlanTasksSchema,
  ActionPlanTemplateSubtaskSchema,
  ActionPlanTemplateTaskSchema,
  RoomCollaboratorSchema,
} from '../../definitions/schemas';
import ActionPlansInterface from '../../graphql/ActionPlans/ActionPlansInterface';
import { ActionPlanTaskStatus } from '../../enums/ActionPlans';
import { CollaboratorType } from '../../enums/CollaboratorType';
import { EventProperty } from '../../tools/Analytics';
import { Properties } from '../../enums/Analytics';

interface Pointers {
  current?: ActionPlanTaskSchema;
  next?: ActionPlanTaskSchema;
  subtasks?: ActionPlanTasksSchema;
}

export const generateCollaborator = (
  collaboratorType: CollaboratorType,
  data: Omit<ActionPlanCollaboratorSchema, 'collaboratorType'>
): ActionPlanCollaboratorSchema => {
  const { id, avatarUri, email, firstName, lastName, photo } = data;
  return {
    id,
    collaboratorType,
    email,
    firstName,
    lastName,
    photo: photo || avatarUri,
  };
};

// build collaborator structure from room collaborator data
export const generateRoomCollaborator = (
  collaboratorType: CollaboratorType,
  data: Omit<RoomCollaboratorSchema, 'collaboratorType'>
): ActionPlanCollaboratorSchema => {
  const { collaboratorId, email = '', firstName, lastName, photo } = data;
  return {
    id: collaboratorId,
    collaboratorType,
    email,
    firstName,
    lastName,
    photo,
  };
};

/**
 * Orders tasks based on the pointer of their previous task. The first task
 * is the one that doesn't point to any other task, from there, each node is
 * then added to the ordered array.
 */
export const orderActionPlanTasks = (
  tasks: ActionPlanTasksSchema,
  skipSubtasks = false
): ActionPlanTasksSchema => {
  const tasksPointers = new Map<string, Pointers>();
  const subtasksMap = new Map<string, ActionPlanTasksSchema>();

  let currentPointer = '';

  tasks.forEach((task) => {
    if (!skipSubtasks && task.parentTaskId) {
      const prevSubtasks = subtasksMap.get(task.parentTaskId) || [];
      subtasksMap.set(task.parentTaskId, [...prevSubtasks, task]);
      return;
    }

    /**
     * If we find the first task (there is no other task pointing to it
     * as its previous), we keep a reference in hand, so we can later build
     * the final ordered array, starting from this task
     */
    if (!task.previousTaskId) {
      currentPointer = task.id;
    }

    /**
     * Setting the pointer of this task in the map. There are 2 scenarios here:
     *  1. Some other task points to this one as being its previous task (making
     *     that task the next one in the ordered array), so there's already an
     *     entry in the Map for this task, so we update the entry to put this task
     *  2. No other task has pointed to this task yet, so we set a new entry in
     *     the Map for this task, so it can later be pointed by other task in the array
     */
    const pointers = tasksPointers.get(task.id);
    tasksPointers.set(task.id, { ...pointers, current: task });

    if (!task.previousTaskId) {
      return;
    }

    /**
     * If this task points to a previous task, we update the pointers of
     * its previous task, in order to set this task as being the next task
     * for that one. This allows to traverse the entries and build the
     * final ordered array, by setting the next item in the array to
     * each task's next pointer
     */
    const prevPointers = tasksPointers.get(task.previousTaskId);
    tasksPointers.set(task.previousTaskId, {
      ...prevPointers,
      next: task,
    });
  });

  const orderedTasks: ActionPlanTasksSchema = [];

  /**
   * Starting from the first task (no other task points to it as its previous),
   * we push it as the first element of the final ordered array. Then, we
   * move the pointer to the next task and repeat for every task in the
   * pointers' Map, eventually leading into a task that doesn't have a next
   * value, meaning we reached the end
   */
  do {
    const { current: task, next } = tasksPointers.get(currentPointer) || {};
    if (!task) {
      break;
    }

    const subtasks = subtasksMap.get(task.id) || [];

    orderedTasks.push({
      ...task,
      subtasks: orderActionPlanTasks(subtasks, true),
    });

    currentPointer = next?.id || '';
  } while (currentPointer !== '');

  return orderedTasks;
};

export const flattenTasks = (
  tasks: ActionPlanTasksSchema
): ActionPlanTasksSchema =>
  tasks.reduce(
    (tasksArr, task) => [...tasksArr, task, ...(task.subtasks || [])],
    [] as ActionPlanTasksSchema
  );

/**
 * Obtains an array of unique collaborators to be used as options for the
 * action plan task owners selection. This combines 2 sources of data:
 * - The potential collaborators: for admins and salespeople in the room
 *   creation/edition flows, these refer to all organization users (members)
 *   and the room contacts.
 * - The existing action plan collaborators: if the action plan already exists.
 */
export const deduplicateCollaborators = (
  potentialCollaborators: ActionPlanCollaboratorsSchema,
  existingCollaborators: ActionPlanCollaboratorsSchema
): ActionPlanCollaboratorsSchema => {
  const optionsMap = new Map<string, ActionPlanCollaboratorSchema>();

  // concatenating both arrays here, because we're accumulating the results
  // in a map of type + id, which ensures no duplicate records will be returned
  [...potentialCollaborators, ...existingCollaborators].forEach(
    (collaborator) => {
      optionsMap.set(
        `${collaborator.collaboratorType}-${collaborator.id}`,
        collaborator
      );
    }
  );

  return Array.from(optionsMap.values());
};

const isActionPlanTaskStatus = (
  value: ActionPlanTaskStatus
): value is ActionPlanTaskStatus =>
  value === ActionPlanTaskStatus.Completed ||
  value === ActionPlanTaskStatus.Pending;

const mapTaskToCreateActionPlanTaskInput = (
  task: ActionPlanTaskSchema
): ActionPlanTemplateTaskSchema => {
  const subtasksList: ActionPlanTemplateSubtaskSchema[] = (
    task.subtasks ?? []
  ).map((st) => ({
    name: st.name,
    description: st.description,
    ...(isActionPlanTaskStatus(st.status) ? { status: st.status } : {}),
  }));

  return {
    name: task.name,
    description: task.description,
    milestone: task.milestone,
    ...(isActionPlanTaskStatus(task.status) ? { status: task.status } : {}),
    subtasks: subtasksList,
  };
};

const mapRootTasksToCreateInput = (
  tasks: ActionPlanTasksSchema | undefined
): ActionPlanTemplateTaskSchema[] => {
  if (!tasks?.length) {
    return [];
  }
  // Pass the full flat task list so orderActionPlanTasks can group rows with
  // parentTaskId into each root's subtasks (filtering to roots only drops them).
  const ordered = orderActionPlanTasks(tasks);
  return ordered.map((task) => mapTaskToCreateActionPlanTaskInput(task));
};

export const createActionPlanCopiesFromPlans = async (
  sourcePlans: ActionPlanSchema[] | undefined
): Promise<ActionPlanSchema[]> => {
  const plans = sourcePlans || [];
  if (!plans.length) {
    return [];
  }

  const plansApi = new ActionPlansInterface();
  return Promise.all(
    plans.map(async (plan) => {
      const tasks = mapRootTasksToCreateInput(plan.tasks);
      const data = await plansApi.create({
        name: plan.name,
        description: plan.description,
        visibility: plan.visibility,
        ...(tasks.length > 0 ? { tasks } : {}),
      });

      const created = data.createActionPlan.actionPlan;
      return created;
    })
  );
};

export const getActionPlansInput = (
  actionPlans: ActionPlanSchema[] | undefined
): ActionPlanInputSchema[] =>
  actionPlans
    ? actionPlans.map(({ id, visibility }) => ({ id, visibility }))
    : [];

export const getTaskBaseProperties = (
  task: ActionPlanTaskSchema,
  referrer: string
): EventProperty => ({
  [Properties.ENTERED_FROM]: referrer,
  [Properties.HAS_DESCRIPTION]: !!task.description,
  [Properties.HAS_DUE_DATE]: !!task.dueDate,
  [Properties.IS_MILESTONE]: task.milestone,
  [Properties.NUMBER_OWNERS]: task.owners?.length || 0,
  [Properties.STATUS]: task.status,
});

export const findCollaborator = (
  collaborators: ActionPlanCollaboratorsSchema,
  id: string | undefined,
  type: CollaboratorType | undefined
) =>
  collaborators.find(
    (collaborator) =>
      collaborator.id === id && collaborator.collaboratorType === type
  );
